<script setup lang="ts">
import PaginationNavigationElement from '~/components/shared/paginationNavigation/elem.vue'

type Props = {
  maxPageCount: number
  paginationCap?: number
  modelValue: number
}

type Emits = {
  'update:modelValue': [urlData: number]
}

const props = withDefaults(defineProps<Props>(), {
  paginationCap: 3,
})
const emit = defineEmits<Emits>()

const route = useRoute()

watch(() => route.query.page, setDataFromUrl)
watch(
  () => props.modelValue,
  () => {
    if (import.meta.client) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  },
)

/*
 * if at the start, selected < 2
 * < 1 2 3 ... 15 >
 * < 1 2 3 ... 15 >
 * if at the start, selected = 3
 * < 1 2 3 4 ... 15 >
 * If in the middle
 * < 1 ... 3 4 5 ... 15 >
 * < 1 ... 10 11 12 ... 15 >
 * If at the end
 * < 1 ... 12 13 14 15 >
 * < 1 ... 13 14 15 >
 * < 1 ... 13 14 15 >
 */
const paginationConfig = computed<[number[], number[], number[]]>(() => {
  if (props.maxPageCount <= props.paginationCap + 1)
    return [buildArray(props.maxPageCount, 0), [], []]

  // Deternime if start mid or end case
  if (props.modelValue < props.paginationCap) {
    // Start
    return [
      buildArray(
        props.modelValue === props.paginationCap - 1
          ? props.paginationCap + 1
          : props.paginationCap,
        0,
      ),
      buildArray(1, props.maxPageCount - 1),
      [],
    ]
  } else if (props.modelValue >= props.maxPageCount - props.paginationCap) {
    // End
    const offset =
      props.modelValue === props.maxPageCount - props.paginationCap
        ? props.paginationCap + 1
        : props.paginationCap

    return [
      buildArray(1, 0),
      buildArray(offset, props.maxPageCount - offset),
      [],
    ]
  } // Mid
  else {
    return [
      buildArray(1, 0),
      buildArray(
        props.paginationCap,
        props.modelValue - (props.paginationCap === 3 ? 1 : 0),
      ),
      buildArray(1, props.maxPageCount - 1),
    ]
  }
})

function buildArray(length: number, startNumber = 0): number[] {
  return Array.from({ length }, (_, i) => startNumber + i + 1)
}

function getUrlData(): number {
  const pageNumber = Number(route.query.page)
  let out: number

  if (Number.isNaN(pageNumber)) {
    out = 0
  } else {
    out =
      pageNumber < 0
        ? 0
        : pageNumber >= props.maxPageCount
          ? props.maxPageCount - 1
          : pageNumber
  }

  if (out !== pageNumber) setUrlFromPageNumber(out)

  return out
}

function setUrlFromPageNumber(page: number) {
  return navigateTo({
    query:
      page !== 0
        ? {
            page: page.toString(),
          }
        : undefined,
  })
}

function setDataFromUrl(): void {
  // this pagination is for SEO pages only
  if (!route.path.includes('/influencer/list')) return

  const urlData = getUrlData()
  if (props.modelValue !== urlData) emit('update:modelValue', urlData)
}

function previousPage(): void {
  if (props.modelValue - 1 >= 0) setUrlFromPageNumber(props.modelValue - 1)
}

function nextPage(): void {
  if (props.modelValue + 1 < props.maxPageCount)
    setUrlFromPageNumber(props.modelValue + 1)
}
</script>

<template>
  <div class="navigationGrid">
    <i
      class="fas fa-chevron-left tw-cursor-pointer tw-text-center"
      :class="{ 'tw-cursor-default tw-text-gray-300': modelValue === 0 }"
      @click="previousPage"
    />
    <PaginationNavigationElement
      v-for="key in paginationConfig[0]"
      :key="key"
      :value="key"
      :current="modelValue + 1"
      @click="setUrlFromPageNumber(key - 1)"
    />
    <div
      v-if="paginationConfig[1].length"
      class="tw-flex tw-items-center tw-justify-center"
    >
      ...
    </div>
    <PaginationNavigationElement
      v-for="key in paginationConfig[1]"
      :key="key"
      :value="key"
      :current="modelValue + 1"
      @click="setUrlFromPageNumber(key - 1)"
    />
    <div
      v-if="paginationConfig[2].length"
      class="tw-flex tw-items-center tw-justify-center"
    >
      ...
    </div>
    <PaginationNavigationElement
      v-for="key in paginationConfig[2]"
      :key="key"
      :value="key"
      :current="modelValue + 1"
      @click="setUrlFromPageNumber(key - 1)"
    />
    <i
      class="fas fa-chevron-right tw-cursor-pointer tw-text-center"
      :class="{
        'tw-cursor-default tw-text-gray-300': modelValue === maxPageCount - 1,
      }"
      @click="nextPage"
    />
  </div>
</template>

<style scoped lang="scss">
.navigationGrid {
  @apply tw-grid tw-w-full tw-items-center tw-justify-center tw-gap-0;

  grid-template-columns: repeat(auto-fit, theme('spacing.8'));
  grid-template-rows: theme('spacing.8');
}
</style>
